import {
  Link,
  Outlet,
  createFileRoute,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { useState } from 'react'
import {
  Button,
  Card,
  Content as CardContent,
  Header as CardHeader,
  Heading as CardTitle,
  DropZone,
  FileTrigger,
  TextField,
} from '@react-spectrum/s2'
import { AlertCircle, FileText, Loader2, Upload } from 'lucide-react'
import { createPlanCheck, listPlanChecks } from '@rpc/plan-check.rpc'
import { trackAnalyticsEvent } from '@client/analytics'
import type { FormEvent } from 'react'
import Footer from '@/components/Footer'
import Header from '@/components/Header'
import {
  fullWidthButtonStyles,
  fullWidthDropZoneStyles,
  fullWidthStyles,
} from '@/styles/s2-macros'

export const Route = createFileRoute('/plan-checks')({
  loader: async () => ({
    runs: await listPlanChecks(),
  }),
  component: PlanChecksPage,
  head: () => ({
    meta: [{ title: 'Plan checks - BuildingRegs.app' }],
  }),
})

const ACCEPTED_PLAN_FILE_TYPES = ['.pdf']

function isAcceptedPlanFile(file: File) {
  return ACCEPTED_PLAN_FILE_TYPES.some((ext) =>
    file.name.toLowerCase().endsWith(ext),
  )
}

export async function getFirstPlanFileFromDrop(event: {
  items: Array<{ kind: string; getFile: () => File | Promise<File> }>
}): Promise<File | null> {
  for (const item of event.items) {
    if (item.kind !== 'file') continue
    return await item.getFile()
  }
  return null
}

function PlanChecksPage() {
  const { runs } = Route.useLoaderData()
  const router = useRouter()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const [title, setTitle] = useState('')
  const [buildingType, setBuildingType] = useState('')
  const [workType, setWorkType] = useState('')
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleFile = (selectedFile: File | null | undefined) => {
    if (!selectedFile) return
    if (!isAcceptedPlanFile(selectedFile)) {
      setError('Please select a PDF plan.')
      trackAnalyticsEvent('plan_check_file_rejected', {
        file_type: selectedFile.type || 'unknown',
      })
      return
    }
    setFile(selectedFile)
    setError(null)
    trackAnalyticsEvent('plan_check_file_selected', {
      file_type: selectedFile.type || 'application/pdf',
      file_size_bytes: selectedFile.size,
    })
    if (!title) setTitle(selectedFile.name.replace(/\.pdf$/i, ''))
  }

  const handleDropZoneFile = async (event: { items: Array<any> }) => {
    handleFile(await getFirstPlanFileFromDrop(event))
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!file) {
      setError('Choose a PDF plan first.')
      trackAnalyticsEvent('plan_check_start_blocked', {
        reason: 'missing_file',
      })
      return
    }
    setIsSubmitting(true)
    setError(null)
    trackAnalyticsEvent('plan_check_started', {
      has_title: Boolean(title.trim()),
      has_building_type: Boolean(buildingType.trim()),
      has_work_type: Boolean(workType.trim()),
      has_notes: Boolean(notes.trim()),
      file_size_bytes: file.size,
    })
    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', title || file.name)
    formData.append('buildingType', buildingType)
    formData.append('workType', workType)
    formData.append('notes', notes)
    try {
      const result = await createPlanCheck({ data: formData })
      trackAnalyticsEvent('plan_check_created', {
        run_id: result.id,
      })
      await router.navigate({
        to: '/plan-checks/$runId',
        params: { runId: result.id },
      })
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : 'Plan check upload failed.',
      )
      trackAnalyticsEvent('plan_check_failed', {
        stage: 'upload',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const content =
    pathname !== '/plan-checks' ? (
      <Outlet />
    ) : (
      <main className="mx-auto grid w-full max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,420px)_1fr] lg:px-8">
        <Card styles={fullWidthStyles}>
          <CardHeader>
            <CardTitle>Run a plan check</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <DropZone
                styles={fullWidthDropZoneStyles}
                onDrop={handleDropZoneFile}
                isDisabled={isSubmitting}
              >
                <div className="flex min-h-44 flex-col items-center justify-center gap-3 p-6 text-center">
                  <Upload className="h-8 w-8 text-neutral-500" />
                  <div>
                    <p className="font-medium">
                      {file ? file.name : 'Drop a PDF floor plan'}
                    </p>
                    <p className="text-sm text-neutral-500">
                      The system will attempt comprehensive coverage against all
                      active checks.
                    </p>
                  </div>
                  <FileTrigger
                    acceptedFileTypes={ACCEPTED_PLAN_FILE_TYPES}
                    onSelect={(files) => handleFile(files?.[0])}
                  >
                    <Button type="button" isDisabled={isSubmitting}>
                      Choose PDF
                    </Button>
                  </FileTrigger>
                </div>
              </DropZone>

              <TextField
                label="Report title"
                value={title}
                onChange={setTitle}
                isDisabled={isSubmitting}
              />
              <TextField
                label="Building type"
                value={buildingType}
                onChange={setBuildingType}
                isDisabled={isSubmitting}
              />
              <TextField
                label="Work type"
                value={workType}
                onChange={setWorkType}
                isDisabled={isSubmitting}
              />
              <TextField
                label="Notes or assumptions"
                value={notes}
                onChange={setNotes}
                isDisabled={isSubmitting}
              />

              {error ? (
                <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-4 w-4" />
                  <span>{error}</span>
                </div>
              ) : null}

              <Button
                type="submit"
                styles={fullWidthButtonStyles}
                isDisabled={isSubmitting || !file}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                Start comprehensive plan check
              </Button>
            </form>
          </CardContent>
        </Card>

        <section className="space-y-4">
          <div>
            <h1 className="text-2xl font-semibold">Plan checks</h1>
            <p className="text-sm text-neutral-500">
              Each run produces a complete coverage matrix, report artifacts,
              and a chat-ready audit trail.
            </p>
          </div>
          <div className="grid gap-3">
            {runs.length === 0 ? (
              <div className="rounded border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">
                No plan checks yet.
              </div>
            ) : (
              runs.map((run) => (
                <Link
                  key={run.id}
                  to="/plan-checks/$runId"
                  params={{ runId: run.id }}
                  onClick={() =>
                    trackAnalyticsEvent('plan_check_opened', {
                      run_id: run.id,
                      status: run.status,
                      location: 'plan_checks_list',
                    })
                  }
                  className="rounded border border-neutral-200 bg-white p-4 transition hover:border-neutral-400"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium text-neutral-950">
                        {run.title}
                      </div>
                      <div className="text-sm text-neutral-500">
                        {run.sourceFilename}
                      </div>
                    </div>
                    <span className="rounded bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-700">
                      {run.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </section>
      </main>
    )

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 dark:bg-neutral-950">
      <Header />
      <div id="main-content" className="flex-1">
        {content}
      </div>
      <Footer />
    </div>
  )
}
